import { applyDefaultButtonAnimation } from 'System/UI/UIHelper';
import Overlay from 'Prototype/UI/Overlay';
import Element from '../../../../Prototype/UI/Element';
import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import { StartAnimation } from 'System/Animation/AnimationHelper';
import { PlaySound } from 'System/Audio/AudioHelper';
import Renderable from 'Component/Renderable';
import { AnimationClipEvent } from 'System/Animation/AnimationClip';
import Game from 'Game';
import { ProductId } from 'Utility/Product';
import TextComponent from 'Component/Render/Text';
import { ContextType } from 'State/Game/ContextType';
import { GameActions } from 'State/Game/GameActions';
import { IAPHelper } from 'Utility/Helpers/IAP/IAPHelper';
import { SeasonPassPurchaseErrorPopup } from './SeasonPassPurchaseErrorPopup';
import { SeasonPassPurchaseReminder } from 'Layouts/Quests/SeasonPass/SeasonPassPurchaseReminder';
import { IGameState } from 'State/Game/IGameState';
import SeasonPassRankScroll from 'Prototype/UI/SeasonPass/SeasonPassRankScroll';
import { SeasonPassReminderRewardItem } from 'Prototype/UI/SeasonPass/SeasonPassReminderRewardItem';
import { PlayerActions } from 'State/Player/PlayerActions';
import { SEASON_PASS_PREMIUM_REMINDER } from 'Constants/SeasonPass';
import DateHelper from 'Utility/DateHelper';
import { getSeasonPassEndDate, getSeasonPassSeed } from 'Utility/SeasonPassHelper';
import SeasonPassTimer from 'System/Timer/Timers/SeasonPassTimer';
import UIScrollComponent from 'Component/UI/Scroll';
import { SeasonPassStateHelper } from 'State/Player/SeasonPass/SeasonPassStateHelper';

type SeasonPassPurchaseReminderProps = {
    state: IGameState;
};

export class SeasonPassPurchaseReminderPopup extends Element {
    private state: IGameState;
    private overlay: Overlay;
    private layout: any;
    private errorPopup: SeasonPassPurchaseErrorPopup;

    public create(props: SeasonPassPurchaseReminderProps): void {
        super.create({
            type: 'group',
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 0, 0, 0],
            },
        });

        this.state = props.state;
        const rewards = SeasonPassStateHelper.getUnClaimedPremiumSeasonPassRewards(this.state);

        this.overlay = GameObject.Instantiate(Overlay, {
            name: 'overlay',
            preventInteraction: true,
            anchors: [0, 0, 0, 0],
            alpha: 0.9,
            color: [0, 0, 0, 225],
        });
        this.addChild(this.overlay);

        this.layout = new SeasonPassPurchaseReminder();
        this.addChild(this.layout.root);

        const { closeButton, purchaseText, purchaseButton, desc1, RewardContainer } = this.layout;
        [closeButton].forEach(o => applyDefaultButtonAnimation(o));
        closeButton.once(GameObjectEvent.POINTER_UP, () => {
            this.destroy();
        });

        const price = Game.sdk.getCatalog().products.find((e) => e.productId === ProductId.PREMIUM_SEASON_PASS).price;
        purchaseText.getComponent(TextComponent).text = price;

        purchaseButton.on(GameObjectEvent.POINTER_UP, () => {
            const product = IAPHelper.CACHED_IAP_REWARDS_MAP()[ProductId.PREMIUM_SEASON_PASS];
            GameActions.setContext(ContextType.PURCHASE_PRODUCT, {
                product,
            });
        });

        if (DateHelper.getTimeUntil(DateHelper.now, getSeasonPassEndDate()) < DateHelper.daysToMilliseconds(this.state.seasonPassData.premiumReminder)) {
            desc1.getComponent(Renderable).visible = true;
            const timer = new SeasonPassTimer();
            timer.onUpdate = (state: IGameState) => {
                if (this.layout.timeText?.getComponent(TextComponent)) {
                    this.layout.timeText.getComponent(TextComponent).text = timer.getFormattedCountdown(state);
                }
            };
            timer.onUpdate(this.state);
        } else {
            desc1.getComponent(Renderable).visible = false;
            this.layout.TimerContainer.getComponent(Renderable).visible = false;
        }

        const boxWidth = 707;
        const boxHeight = 140;
        const itemWidth = 150;

        const scrollArea: Element = GameObject.Instantiate(SeasonPassRankScroll, {
            name: 'season_pass_reminder_scroll',
            layout: {
                anchors: [0, 0, 0, 0],
                pivot: [0, 0],
                offsets: [353, 65, 353, 65],
                constrains: {
                    heightMin: boxHeight,
                    heightMax: boxHeight,
                    widthMin: boxWidth,
                    widthMax: boxWidth,
                },
            },
            scrollWidth: itemWidth * rewards.length,
            scrollHeight: boxHeight,
        });

        RewardContainer.addChild(scrollArea);

        for (let i = 0; i < rewards.length; i++) {
            const item = GameObject.Instantiate(SeasonPassReminderRewardItem, { state: this.state, key: i, reward: rewards[i], itemWidth });
            scrollArea.addChild(item);
        }

        this.layout.ArrowPrevious.getComponent(Renderable).visible = false;
        this.layout.ArrowNext.getComponent(Renderable).visible = rewards.length > 4;
        scrollArea.container.resize(0, 0);
        scrollArea.getComponent(UIScrollComponent).UIComponent.setScrollEndListener((x, y, isEnd) => {
            this.layout.ArrowPrevious.getComponent(Renderable).visible = x < 0;
            this.layout.ArrowNext.getComponent(Renderable).visible = !isEnd;
        });

        this.layout.ArrowPrevious.on(GameObjectEvent.SELECTED, () => {
            const currentScrollPosition = scrollArea.getComponent(UIScrollComponent).UIComponent.getScrollPosition().x;
            scrollArea.getComponent(UIScrollComponent).UIComponent.scrollToPosition(currentScrollPosition + itemWidth, 0);
        });

        this.layout.ArrowNext.on(GameObjectEvent.SELECTED, () => {
            const currentScrollPosition = scrollArea.getComponent(UIScrollComponent).UIComponent.getScrollPosition().x;
            scrollArea.getComponent(UIScrollComponent).UIComponent.scrollToPosition(currentScrollPosition - itemWidth, 0);
        });

        const timer = new SeasonPassTimer();
        timer.onUpdate = (state: IGameState) => {
            if (this.layout.timeText?.getComponent(TextComponent)) {
                this.layout.timeText.getComponent(TextComponent).text = timer.getFormattedCountdown(state);
            }
        };
        timer.onUpdate(this.state);

        PlayerActions.setFlag({ id: SEASON_PASS_PREMIUM_REMINDER, expiryDate: DateHelper.getNextDay(DateHelper.now) });
        PlayerActions.setFlag({ id: getSeasonPassSeed() + SEASON_PASS_PREMIUM_REMINDER + this.state.playerState.seasonPass.currentRank, expiryDate: DateHelper.getDateFromValue(this.state.playerState.seasonPass.endDate) });

        this.show();
    }

    public show() {
        this.getComponent(Renderable).visible = false;
        this.overlay.getComponent(Renderable).alpha = 0;
        PlaySound('swoosh-1');

        StartAnimation(this, 'MenuShow');
        StartAnimation(this.overlay, 'OverlayFadeIn', { delay: 250 });
    }

    public destroy(): void {
        if (!this.destroyed) {
            PlaySound('swoosh-2');
            StartAnimation(this.overlay, 'OverlayFadeOut')
            StartAnimation(this, 'MenuHide', { delay: 350 }).once(AnimationClipEvent.COMPLETE, () => {
                super.destroy();
            });
        }
    }

    public showPurchaseError(error: string) {
        this.errorPopup = GameObject.Instantiate(SeasonPassPurchaseErrorPopup, { errorMessage: error });
        this.layout.root.addChild(this.errorPopup);
    }

    public hidePurchaseError() {
        if (this.errorPopup) {
            this.errorPopup.destroy();
            this.errorPopup = null;
        }
    }
}
